/**
 * Mobile install + fullscreen helpers for the pad PWA.
 *
 *   beforeinstallprompt        — captured at module load, replayed by
 *                                triggerInstall() inside a user gesture.
 *                                Android Chrome only; iOS Safari ignores
 *                                it (no programmatic install on iOS).
 *
 *   requestFullscreen          — only works inside a user gesture chain
 *                                AND on browsers that implement the API.
 *                                iOS Safari rejects: standalone PWA
 *                                install is the only path to true
 *                                fullscreen on iOS.
 *
 * Ported from asteroid-sats's install-fullscreen.ts so the pad gets
 * the same surface even though it's a separate Vite app.
 */

interface InstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

let deferredInstallPrompt: InstallPromptEvent | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e as InstallPromptEvent;
  });
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
  });
}

/** True iff the page is running as an installed PWA (standalone window). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  const navStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return navStandalone === true;
}

/** iOS Safari (not Chrome/Firefox on iOS). Needed because only Safari
 *  exposes the Share → Add to Home Screen flow. */
export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  if (!isIos) return false;
  const isSafari = /Safari\//.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isSafari;
}

/** Touch-capable + narrow viewport — the only place install / fullscreen
 *  nags make sense. Desktop visitors get to see the controller without
 *  being asked to install anything. */
export function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const narrow = window.matchMedia('(max-width: 900px)').matches;
  return touch && narrow;
}

export function canInstallNow(): boolean {
  return deferredInstallPrompt !== null;
}

/** Replay the captured install prompt. Must be called inside a user
 *  gesture (button click). Resolves true if the user accepted. */
export async function triggerInstall(): Promise<boolean> {
  const ev = deferredInstallPrompt;
  if (!ev) return false;
  await ev.prompt();
  const choice = await ev.userChoice;
  if (choice.outcome === 'accepted') {
    deferredInstallPrompt = null;
    return true;
  }
  return false;
}

/** Fire-and-forget fullscreen request — call inside a click handler.
 *  Silently no-ops on iOS Safari (no API). */
export function tryEnterFullscreen(): void {
  if (typeof document === 'undefined') return;
  const docEl = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  const d = document as Document & { webkitFullscreenElement?: Element };
  if (d.fullscreenElement || d.webkitFullscreenElement) return;
  if (docEl.requestFullscreen) {
    docEl.requestFullscreen().catch(() => { /* user rejected / blocked */ });
  } else if (docEl.webkitRequestFullscreen) {
    try { docEl.webkitRequestFullscreen(); } catch { /* ignore */ }
  }
}

export function hasFullscreenAPI(): boolean {
  if (typeof document === 'undefined') return false;
  const docEl = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  return typeof docEl.requestFullscreen === 'function'
      || typeof docEl.webkitRequestFullscreen === 'function';
}
